import { store } from '../root/ReduxStore.store';

export type ProductRootState = ReturnType<typeof store.getState>;

export interface ProductsRequest {
  params: ProductsRequestParams;
  workspaceId: string;
  body: ProductsRequestBody;
  customerId?: string;
}

export interface ProductsRequestParams {
  pageNo?: number;
  pageSize?: number;
  customerId?: string;
  sellerWorkspaceId?: string;
}

export interface ProductsRequestBody {
  includeFacets?: boolean;
  searchKey?: string;
  includeDivisions?: boolean;
  includeCfas?: boolean;
  includeCollections?: boolean;
  includeCategoryCodesList?: boolean;
  includeCategoryTreeList?: boolean;
  includeFields?: string[];
  categoryCodes?: string[];
  divisionIdFilter?: string[];
  divisionOperator?: boolean;
  divisionIds?: string[];
  facetValueIds?: string[];
  facetValueOperator?: boolean;
  collectionId?: string;
  skuCode?: string | number;
  productVariantId?: string | number;
  statusFilter?: string;
  sortDirection?: string;
  sortBy?: string;
  customerId?: string;
  productVariantIds?: number[];
  groupByProduct?: boolean;
  schemeType?: string[];
  segmentIds?: string[];
  priceListId?: string;
}

export interface CategoryFilterItem {
  categoryCode: string;
  categoryId: string;
  categoryName: string;
  selected?: boolean;
}
export interface CategoryTreeItem {
  id?: string;
  name?: string;
  categoryCode: string;
  description?: string;
  parentId?: string | null;
  children?: CategoryTreeItem[];
}

export interface Facets {
  createdAt?: string;
  updatedAt?: string;
  isPrivate?: boolean;
  code?: string;
  id: number;
  values: {
    createdAt?: string;
    updatedAt?: string;
    code: string;
    id: number;
    productVariantsCount?: number;
    name: string;
    facetValue?: string;
    facetValueCode?: string;
  }[];
  name: string;
  facetCode?: string;
}

export interface ProductListResponse {
  total: number;
  startRecord: number;
  endRecord: number;
  products: Product[];
  divisionSummary: ProductDivision[];
  categoryCodes: CategoryFilterItem[];
  categoryTree?: CategoryTreeItem[];
  facets: Facets[];
  cfaSummary: ProductCfa[];
  status: string;
  statusCode: number;
  input: ProductInput;
  segmentsSummary: ProductSegment[];
}

export interface ProductCfa {
  cfaCode: string;
  cfaId: string;
  supplierName: string;
  minOrderQty: number;
  isDefault: boolean;
}

export interface ProductDivision {
  divisionCode: string;
  divisionId: string;
  name: string;
  headDivisionId: string;
  headDivision: ProductHeadDivision;
  isDefault: boolean;
}

export interface ProductHeadDivision {
  id: string;
  code: string;
  name: string;
}

export interface ProductInput {
  skip: number;
  sort: {
    productId: string;
  };
  take: string;
}

export interface Product {
  parentSku: string;
  productAsset: Record<string, any>;
  source: string;
  preview: string;
  enabled: boolean;
  id: number;
  productVariants: ProductVariant[];
  channelId: number;
  workspaceId: string;
}

export enum PricingStrategy {
  STANDARD = 'STANDARD',
  WEIGHT_BASED = 'WEIGHT_BASED',
}

export interface ProductVariant {
  facets: Facets[];
  taxCategory: ProductTaxCategory;
  enabled?: boolean;
  price: number;
  basePrice?: number;
  pricingStrategy?: PricingStrategy;
  MRP: number;
  PTR: number;
  isSpecial?: boolean;
  sku: string;
  orderId?: number;
  sortOrder: number;
  id: number;
  taxCategoryId: number;
  productId: number;
  name: string;
  shortName: string;
  shortDescription: string;
  productVariantId: number;
  parentProductId?: number;
  ean?: string;
  erpId?: string;
  erpPriceUOM?: string;
  erpUOM?: string;
  ZINS?: boolean;
  hsnCode?: string;
  upcCode?: string;
  baseUom: string;
  additionalUom?: string;
  additionalUOM?: string;
  warrentyInformation: string;
  condition: string;
  packSize: number;
  manufacturingDate?: null;
  expiryDate?: null;
  bestBeforeOrAfter?: null;
  bestAfter?: null;
  minOrderQty: number;
  maxOrderQty?: number;
  minRemShelfLife?: null;
  qtyMultiplier?: number;
  grossWeight?: number;
  netWeight?: number;
  volume?: number;
  division: ProductDivision[];
  cfas: ProductCfa[];
  collections?: string[];
  promotions: ProductPromotion[];
  stock?: number;
  sales?: number;
  margin?: string;
  isActiveForOrderCfaIds?: string[];
  salesOrg?: null;
  assets: {
    filename: string;
    redirectUrl: string;
    sequenceNo: number;
    subType: string;
  }[];
  categories: ProductVariantCategory[];
  quantity: number;
  popularity?: number;
  isDefault?: boolean;
  newQty?: number;
  segments?: ProductSegment[];
  baseUOM?: string;
}

export interface ProductPromotion {
  id: number;
  name: string;
  promotionType: string;
  promotionCode: string;
  enabled: boolean;
  startsAt: Date;
  endsAt: Date;
  productVariantId: number;
  discount: string;
  discountType: string;
  minQtyASC: number;
  freeQtyASC: number;
  promotionSubType: null;
  promotion: string;
}

export interface ProductTaxCategory {
  id: number;
  name: string;
}

export interface LineInOrder {
  quantity: number;
  productVariantId: number;
  orderId: number;
  poFileId?: string;
  poFileLineId?: string;
  orderLineId?: number;
}

export enum ProductSegmentMetric {
  NON_MOVING = 'Non Moving',
  NEW_LAUNCH = 'New Launch',
  SLOW_MOVING = 'Slow Moving',
  FAST_MOVING = 'Fast Moving',
  TOP_GROSSING = 'Top Grossing',
}

export interface ProductSegment {
  segmentId: string;
  segmentName: ProductSegmentMetric;
}

export interface CatalogListData {
  sku: string;
  name: string;
  productVariantId: number;
  minOrderQty: number;
  isAddedToCart: boolean;
  addedQty: number;
  lineInOrder: LineInOrder;
  pts?: number;
  ptr?: number;
  mrp?: number;
  cfas?: ProductCfa[];
  isActiveForOrderCfaIds?: string[];
  margin?: number;
  offer?: boolean;
  moqError?: boolean;
  promotions?: ProductPromotion[];
  discountedPrice?: number;
  productImage?: string;
  updatingQty?: boolean;
  isLoading?: boolean;
  isDeleting?: boolean;
  categories?: ProductVariantCategory[];
  productId: number;
  pvIds: number[];
  accQty: number;
  facets: Facets[];
  segments: ProductSegment[];
  weightedPrice?: number;
  pricingStrategy?: PricingStrategy;
  stock: number;
}

export interface ProductVariantCategory {
  categoryId: number;
  categoryName: string;
  categoryCode: string;
}

export interface CatalogueOrderSummary {
  poFileId: string;
  orderId: number;
  skuCount: number;
  totalQuantity: number;
}

export enum SchemeTypes {
  BUYXGETY = 'buy_x_get_y_free',
  PRODUCTDISCOUNT = 'products_percentage_discount',
}

export interface CatalogProducts {
  name: string;
  image: string;
  price: number;
  promotions: CatalogProductsPromotion[];
  tags: any[];
  qtyInCart: number;
}

export interface CatalogProductsPromotion {
  id: number;
  name: string;
  promotionType: string;
  promotionCode: string;
  enabled: boolean;
  startsAt: any;
  endsAt: any;
  productVariantId: number;
  discount: string;
  discountType: any;
  minQtyASC: number;
  freeQtyASC: number;
  promotionSubType: number;
  promotion: string;
}

export interface ParseImageToProductsRequest {
  workspaceId: string;
  sellerWorkspaceId: string;
  body: {
    fileName: string;
    mimeType: string;
    url: string;
  };
}

export interface ParseImageToProductsResponse {
  items: PIItems;
}

export interface PIItems {
  matches: PIMatch[];
  totalMatches: number;
}

export interface PIMatch {
  originalInput: PIOriginalInput;
  matchedProducts: PIMatchedProduct[];
}

export interface PIOriginalInput {
  title: string;
  quantity: number;
}

export interface PIMatchedProduct {
  productId: number;
  variantId: number;
  title: string;
  moq: number;
  price: number;
  imageUrl: string;
  adjustedQuantity?: number;
  isLoading?: boolean;
}

export interface getProductDetailsRequest {
  includeFacets: boolean;
  includeDivisions: boolean;
  includeCfas: boolean;
  groupByProduct?: boolean;
  productVariantId?: number;
  productIds?: string[];
}

export interface getProductDetailsResponse {
  products: Product[];
}

export interface ProductDetails {
  title: string;
  price: number;
  tags: ProductVariantCategory[];
  description: string;
  details: {
    ptr: string;
    margin: string;
    moq: number;
    weightedPrice?: number;
    pricingStrategy?: PricingStrategy;
  };
  images: string[];
  itemInCart?: {
    quantity: number;
    orderId: number;
  };
  stock: number;
  productVariantId: number;
  promotions: ProductPromotion[] | undefined;
  facets: Facets[];
  segments: ProductSegment[];
}

export interface ProductDetailsV2 {
  title: string;
  price: number;
  tags: ProductVariantCategory[];
  description: string;
  details: {
    ptr: string;
    margin: string;
    moq: number;
    weightedPrice?: number;
    pricingStrategy?: PricingStrategy;
  };
  images: string[];
  itemInCart?: {
    quantity: number;
    orderId?: number;
    staging?: boolean;
    inCart?: boolean;
  };
  productVariantId: number;
  promotions: ProductPromotion[] | undefined;
  facets: Facets[];
  segments: ProductSegment[];
  stock: number;
}

export interface ProductFacetDetails {
  facetName: string;
  facetCode: string;
  facetValues: {
    createdAt?: string;
    updatedAt?: string;
    code: string;
    id: number;
    productVariantsCount?: number;
    name: string;
    facetValue?: string;
    facetValueCode?: string;
    isActive: boolean;
  }[];
}

export interface ProductFacetMap {
  [key: string]: ProductFacetDetails;
}

export enum ProductCardVariant {
  ORDER_ADD = 'order-add',
  ORDER_MAP = 'order-map',
  ORDER_REPLACE = 'order-replace',
  STOCK = 'stock',
  EDIT_PRICE = 'edit-price',
  SHARE_PRODUCT = 'share-product',
  VIEW_PRODUCT = 'view-product',
  ADD_TO_OFFLINE_CART = 'add-to-offline-cart',
  CUSTOMER_CART = 'customer-cart',
}

export interface CreateProductResponse {
  product: number;
  productVariantId: number;
  status: string;
  statusCode: number;
  message: string;
}

export interface CreateProductRequest {
  workspaceId: string;
  parentSku?: string;
  name?: string;
  id?: number;
  productVariants: ProductVariantList[];
  divisionCFACodes?: {
    cfaCode: string;
    divisionCode: string;
    isActive: boolean;
  }[];
  categories?: Categories[];
  productAttributes?: ProductAttribute[];
}

interface ProductVariantList {
  name?: string;
  sku?: string;
  sortOrder?: number;
  shortName?: string;
  shortDescription?: string;
  listPrice?: number;
  taxCategoryId?: number;
  taxCategoryCode?: string;
  hsnCode?: string;
  baseUom?: string;
  packSize?: number;
  minOrderQty?: number;
  maxOrderQty?: number;
  qtyMultiplier?: number;
  grossWeight?: number;
  netWeight?: number;
  volume?: number;
  warrentyInformation?: string;
  condition?: string;
  facets?: any[];
  MRP?: number;
  upcCode?: null | string;
  taxCategory?: ProductsTaxCategory;
  enabled?: boolean;
  price: number;
  ZINS?: boolean;
  PTR?: number;
  collections?: string[];
  isSpecial?: string;
  stockOnHand?: number;
  sales?: number;
  promotions?: Promotion[];
  packingInfo?: any;
  minRemShelfLife?: number;
  id?: number;
  productId?: number;
  productVariantId?: number;
  manufacturingDate?: string;
  expiryDate?: string;
  bestBeforeOrAfter?: string;
  bestAfter?: string;
  ean?: string;
  erpId?: string;
  erpUOM?: string;
  erpPriceUOM?: string;
  assets?: ProductAsset[];
}

interface ProductAsset {
  fileName: string;
  mimeType: string;
  sequence: number;
  type: string;
  subType: string;
  isArchived: number;
  isNewAsset: number;
}
interface ProductsTaxCategory {
  createdAt?: string;
  updatedAt?: string;
  name: string;
  code?: string;
  isDefault?: boolean;
  id?: number;
}

interface Promotion {
  id: number;
  name: string;
  promotionType: string;
  promotionCode: string;
  enabled: boolean;
  startsAt: string;
  endsAt: string;
  productVariantId: number;
  discount: string;
  discountType: string;
  minQtyASC: number;
  freeQtyASC: number;
  promotion: string;
}

interface Categories {
  category: string;
  subCategory?: {
    category: string;
    subCategory?: {
      category: string;
    };
  };
}

interface ProductAttribute {
  baseUom: string;
  packSize: number;
}

export interface BulkProductUpdateRequest {
  sellerWorkspaceId: string;
  items: Omit<CreateProductRequest, 'workspaceId'>[];
}

export enum SelectedTemplateType {
  PARENT_PRODUCT_CATALOGUE = 'parent_product_catalogue',
  PRODUCT_CATALOGUE = 'product_catalogue',
  CATALOGUE_LIST_VIEW = 'catalogue_List_View',
}

export type GenerateProductCatalogPDFResponse = string;
export interface GenerateProductCatalogPDFRequest {
  params: {
    sellerWorkspaceId: string;
  };
  body: { templateName: SelectedTemplateType; productVariantIds: string[] };
}

export type GenerateCustomerPricingPDFResponse = string;
export interface GenerateCustomerPricingPDFRequest {
  params: {
    sellerWorkspaceId: string;
  };
  body: {
    templateName: SelectedTemplateType;
    productVariantIds: string[];
    customerId: string;
  };
}

export interface AddLineItems {
  productVariantId: number;
  quantity: number;
  productMOQ: number;
  replaceProductVariantId?: number;
}

export interface ParseFileBody {
  fileName: string;
  mimeType: string;
  url: string;
  extension: string;
}

export interface ParseFileRequest {
  sellerWorkspaceId: string;
  body: ParseFileBody;
}
